/**
 * Phase 50 Plan 09 — SLA Breach Dashboard page.
 *
 * Displays tasks past their SLA resolution target, grouped by tier × project.
 * Default range: last 30 days (BD-DATE-01: tasks created in [from, to] that
 * are currently breaching their tier's resolutionTarget).
 *
 * URL-driven filters (useSearchParams) with sessionStorage `breach.dateRange`
 * backup for resilience on page reload.
 *
 * Row click navigates to /projects/:projectId/board?openTask=:taskId — the
 * `openTask` param spelling is CRITICAL: KanbanBoardPage.tsx:236 reads
 * `searchParams.get('openTask')`. Any other spelling silently fails to open
 * TaskDetailSlideOver (BD-01 acceptance criterion).
 */
import { useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { Spinner } from '../../components/common/Spinner';
import { DayMonthYearPicker } from '../../components/ui/DayMonthYearPicker';
import { useSlaBreachDashboard } from '../../hooks/useSlaBreachDashboard';
import { getTierColor, chipTextColor } from '../../utils/format';
import type { SlaBreachRow } from '../../services/api/slaBreachDashboard';

const STORAGE_KEY = 'breach.dateRange';
const DEFAULT_RANGE_DAYS = 30;

function defaultFrom(): string {
  return format(subDays(new Date(), DEFAULT_RANGE_DAYS), 'yyyy-MM-dd');
}

function defaultTo(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

interface TierGroup {
  tier: { id: number; name: string; orderIndex: number };
  byProject: Map<number, { projectName: string; rows: SlaBreachRow[] }>;
}

export function SlaBreachDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const from = searchParams.get('from') ?? defaultFrom();
  const to = searchParams.get('to') ?? defaultTo();

  const setFilter = useCallback(
    (key: 'from' | 'to', value: string) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      setSearchParams(params, { replace: true });

      // sessionStorage backup — survives URL clear / in-app navigation
      try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}') as Record<
          string,
          string
        >;
        stored[key] = value;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch {
        // sessionStorage not available (private browsing, etc.) — ignore silently
      }
    },
    [searchParams, setSearchParams],
  );

  const query = useSlaBreachDashboard({ from, to });

  // Group rows by tier × project; sort tier groups by orderIndex asc (P1 first)
  const grouped = useMemo((): TierGroup[] => {
    const rows = query.data?.rows ?? [];
    const byTier = new Map<number, TierGroup>();

    for (const r of rows) {
      let g = byTier.get(r.tierId);
      if (!g) {
        g = {
          tier: { id: r.tierId, name: r.tierName, orderIndex: r.tierOrderIndex },
          byProject: new Map(),
        };
        byTier.set(r.tierId, g);
      }
      let p = g.byProject.get(r.projectId);
      if (!p) {
        p = { projectName: r.projectName, rows: [] };
        g.byProject.set(r.projectId, p);
      }
      p.rows.push(r);
    }

    return Array.from(byTier.values()).sort((a, b) => a.tier.orderIndex - b.tier.orderIndex);
  }, [query.data]);

  return (
    <div className="p-4">
      <h1 className="text-xl font-semibold mb-1">SLA Breach Dashboard</h1>
      <p className="text-sm text-gray-500 mb-4">
        Tasks past their tier resolution target. Default range: last {DEFAULT_RANGE_DAYS} days.
      </p>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded p-4 mb-4 flex flex-wrap gap-4 items-end">
        <div>
          <span className="block text-xs text-gray-600 mb-1">From</span>
          <DayMonthYearPicker
            value={from}
            onChange={(v) => setFilter('from', v)}
            label="From date"
          />
        </div>
        <div>
          <span className="block text-xs text-gray-600 mb-1">To</span>
          <DayMonthYearPicker value={to} onChange={(v) => setFilter('to', v)} label="To date" />
        </div>
      </div>

      {/* Error banner */}
      {query.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 mb-3 text-sm">
          Failed to load breach dashboard.
        </div>
      )}

      {/* Content */}
      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-sm text-gray-500">No breaching tasks in the selected range.</div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => {
            const bg = getTierColor(g.tier.orderIndex);
            const fg = chipTextColor(bg);
            return (
              <div key={g.tier.id}>
                <h2 className="flex items-center gap-2 text-sm font-semibold mb-2">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-[11px]"
                    style={{ backgroundColor: bg, color: fg }}
                  >
                    P{g.tier.orderIndex}
                  </span>
                  <span>{g.tier.name}</span>
                </h2>

                {Array.from(g.byProject.values()).map((p) => (
                  <div key={p.projectName} className="ml-4 mb-3">
                    <h3 className="text-xs uppercase tracking-wide text-gray-400 mb-1">
                      {p.projectName}
                    </h3>
                    <table className="w-full text-sm border border-gray-200 rounded">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-2 py-1 font-medium text-xs">Task</th>
                          <th className="text-left px-2 py-1 font-medium text-xs">Assignee</th>
                          <th className="text-right px-2 py-1 font-medium text-xs">
                            Elapsed (min)
                          </th>
                          <th className="text-right px-2 py-1 font-medium text-xs">
                            Target (min)
                          </th>
                          <th className="text-right px-2 py-1 font-medium text-xs">Overdue %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.rows.map((r) => (
                          <tr
                            key={r.taskId}
                            className="cursor-pointer hover:bg-gray-50 border-t border-gray-100"
                            onClick={() =>
                              navigate(`/projects/${r.projectId}/board?openTask=${r.taskId}`)
                            }
                          >
                            <td className="px-2 py-1.5">
                              {r.taskCode ? (
                                <span className="text-gray-400 mr-1 text-xs">{r.taskCode}</span>
                              ) : null}
                              {r.taskTitle}
                            </td>
                            <td className="px-2 py-1.5 text-gray-600">
                              {r.assigneeName ?? '—'}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              {Math.round(r.resolutionElapsedMs / 60_000)}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              {r.resolutionTargetMinutes}
                            </td>
                            <td className="px-2 py-1.5 text-right text-rose-600 font-semibold">
                              {Math.round(r.overduePct)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SlaBreachDashboardPage;
